import holviIcon from '@assets/holvi-icon-light.svg'
import ifInsuranceIcon from '@assets/if-insurance.svg'
import {
  getIfInsurancePageUrl,
  getInvoicingPageUrl,
  getSettingsPageUrl,
  getVATPageUrl
} from '@constants'
import { DashboardAlertIntents, DashboardAlertTypes, IDashboardAlert } from '@query'
import { NocfoTheme } from '@root/styles/styled'
import React from 'react'
import { FaCalculator, FaDotCircle, FaFlagCheckered, FaLink, FaMask } from 'react-icons/fa'
import { FaArrowTrendUp, FaClockRotateLeft, FaRotate } from 'react-icons/fa6'

const PARTNER_TYPES = new Set<DashboardAlertTypes>([
  DashboardAlertTypes.IF_INSURANCE_NEW_BUSINESS,
  DashboardAlertTypes.IF_INSURANCE_TENDERING,
  DashboardAlertTypes.HOLVI_ACCOUNT_CREATION,
  DashboardAlertTypes.HOLVI_INTEGRATION_RENEWAL
])

const DISMISSABLE_TYPES = new Set<DashboardAlertTypes>([
  DashboardAlertTypes.IF_INSURANCE_NEW_BUSINESS,
  DashboardAlertTypes.IF_INSURANCE_TENDERING,
  DashboardAlertTypes.NEW_BUSINESS,
  DashboardAlertTypes.PLAN_RECOMMENDATION
])

const ICONS: Partial<Record<DashboardAlertTypes, React.ReactNode>> = {
  [DashboardAlertTypes.BANK_INTEGRATION_CREATION]: <FaRotate />,
  [DashboardAlertTypes.BANK_INTEGRATION_RENEWAL]: <FaLink />,
  [DashboardAlertTypes.BANK_INTEGRATION_EXPIRED]: <FaLink />,
  [DashboardAlertTypes.BANK_INTEGRATION_TO_BE_EXPIRED]: <FaLink />,
  [DashboardAlertTypes.HOLVI_INTEGRATION_RENEWAL]: <FaClockRotateLeft />,
  [DashboardAlertTypes.VAT_PERIOD_MUST_BE_REPORTED]: <FaMask />,
  [DashboardAlertTypes.VAT_PERIOD_REPORTING_PENDING]: <FaMask />,
  [DashboardAlertTypes.EU_RECAPITULATIVE_PENDING]: <FaMask />,
  [DashboardAlertTypes.UNPAID_PURCHASE_INVOICES]: <FaCalculator />,
  [DashboardAlertTypes.UNPAID_SALES_INVOICES]: <FaCalculator />,
  [DashboardAlertTypes.NEW_BUSINESS]: <FaFlagCheckered />,
  [DashboardAlertTypes.PLAN_RECOMMENDATION]: <FaArrowTrendUp />
}

const LOGOS: Partial<Record<DashboardAlertTypes, string>> = {
  [DashboardAlertTypes.IF_INSURANCE_NEW_BUSINESS]: ifInsuranceIcon,
  [DashboardAlertTypes.IF_INSURANCE_TENDERING]: ifInsuranceIcon,
  [DashboardAlertTypes.HOLVI_ACCOUNT_CREATION]: holviIcon
}

const CTA_KEYS: Partial<Record<DashboardAlertTypes, string>> = {
  [DashboardAlertTypes.BANK_INTEGRATION_CREATION]: 'connectBank',
  [DashboardAlertTypes.BANK_INTEGRATION_RENEWAL]: 'renewConnection',
  [DashboardAlertTypes.BANK_INTEGRATION_EXPIRED]: 'renewConnection',
  [DashboardAlertTypes.BANK_INTEGRATION_TO_BE_EXPIRED]: 'renewConnection',
  [DashboardAlertTypes.HOLVI_INTEGRATION_RENEWAL]: 'renewConnection',
  [DashboardAlertTypes.VAT_PERIOD_MUST_BE_REPORTED]: 'openVatPeriod',
  [DashboardAlertTypes.VAT_PERIOD_REPORTING_PENDING]: 'openVatPeriod',
  [DashboardAlertTypes.EU_RECAPITULATIVE_PENDING]: 'openVatPeriod',
  [DashboardAlertTypes.UNPAID_SALES_INVOICES]: 'openInvoices',
  [DashboardAlertTypes.UNPAID_PURCHASE_INVOICES]: 'openInvoices',
  [DashboardAlertTypes.IF_INSURANCE_NEW_BUSINESS]: 'requestOffer',
  [DashboardAlertTypes.IF_INSURANCE_TENDERING]: 'requestOffer',
  [DashboardAlertTypes.HOLVI_ACCOUNT_CREATION]: 'openAccount',
  [DashboardAlertTypes.NEW_BUSINESS]: 'getStarted',
  [DashboardAlertTypes.PLAN_RECOMMENDATION]: 'viewPlans'
}

export interface AlertPresentation {
  icon: React.ReactNode
  logo?: string
  color: string
  tint: string
  isPartner: boolean
  isDismissable: boolean
  ctaKey: string
  link: string
}

export const getAlertPresentation = (
  alert: IDashboardAlert,
  theme: NocfoTheme,
  slug: string
): AlertPresentation => {
  const { color, tint } = getColors(alert, theme)

  return {
    icon: ICONS[alert.type] ?? <FaDotCircle />,
    logo: LOGOS[alert.type],
    color,
    tint,
    isPartner: PARTNER_TYPES.has(alert.type),
    isDismissable: DISMISSABLE_TYPES.has(alert.type),
    ctaKey: CTA_KEYS[alert.type] ?? 'open',
    link: getAlertLink(alert, slug)
  }
}

const getColors = (alert: IDashboardAlert, theme: NocfoTheme) => {
  if (
    alert.type === DashboardAlertTypes.IF_INSURANCE_NEW_BUSINESS ||
    alert.type === DashboardAlertTypes.IF_INSURANCE_TENDERING
  ) {
    return { color: theme.colors.ifBlue, tint: theme.dashboard.infoTint }
  }
  if (alert.type === DashboardAlertTypes.HOLVI_ACCOUNT_CREATION) {
    return { color: theme.colors.neutralWhite, tint: theme.colors.menuBg }
  }

  switch (alert.intent) {
    case DashboardAlertIntents.ERROR:
      return {
        color: theme.dashboard.critical,
        tint: theme.dashboard.criticalTint
      }
    case DashboardAlertIntents.WARNING:
      return {
        color: theme.dashboard.caution,
        tint: theme.dashboard.cautionTint
      }
    case DashboardAlertIntents.SUCCESS:
      return {
        color: theme.dashboard.positive,
        tint: theme.dashboard.positiveTint
      }
    default:
      return {
        color: theme.dashboard.infoDeep,
        tint: theme.dashboard.infoTint
      }
  }
}

const getAlertLink = (alert: IDashboardAlert, slug: string): string => {
  if (alert.link) return alert.link

  switch (alert.type) {
    case DashboardAlertTypes.BANK_INTEGRATION_CREATION:
    case DashboardAlertTypes.BANK_INTEGRATION_RENEWAL:
    case DashboardAlertTypes.BANK_INTEGRATION_EXPIRED:
    case DashboardAlertTypes.BANK_INTEGRATION_TO_BE_EXPIRED:
      return getSettingsPageUrl(slug, { page: 'integrations' })
    case DashboardAlertTypes.HOLVI_INTEGRATION_RENEWAL:
      return getSettingsPageUrl(slug, { page: 'holvi' })
    case DashboardAlertTypes.VAT_PERIOD_MUST_BE_REPORTED:
    case DashboardAlertTypes.VAT_PERIOD_REPORTING_PENDING:
    case DashboardAlertTypes.EU_RECAPITULATIVE_PENDING:
      return getVATPageUrl(slug, { id: alert.metadata?.vat_period_id })
    case DashboardAlertTypes.UNPAID_SALES_INVOICES:
    case DashboardAlertTypes.UNPAID_PURCHASE_INVOICES:
      return getInvoicingPageUrl(slug)
    case DashboardAlertTypes.IF_INSURANCE_NEW_BUSINESS:
    case DashboardAlertTypes.IF_INSURANCE_TENDERING:
      return getIfInsurancePageUrl(slug)
    default:
      return `/${slug}`
  }
}
